import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, UserCheck, IndianRupee, UserPlus, ArrowRight } from 'lucide-react';
import { members, getMemberStats, formatINR } from '../../data/members';
export function AdminDashboard() {
  const stats = getMemberStats();
  const maxTier = Math.max(...stats.tierBreakdown.map(t => t.count));
  const recentMembers = [...members].sort((a, b) => b.joinedTimestamp - a.joinedTimestamp).slice(0, 6);
  const membersWithDues = members.filter(m => m.outstandingBalance > 0).sort((a, b) => b.outstandingBalance - a.outstandingBalance).slice(0, 6);
  const kpis = [{
    label: 'Total Members',
    value: stats.total.toLocaleString('en-IN'),
    icon: Users,
    accent: 'text-club-green'
  }, {
    label: 'Active Members',
    value: stats.activeCount.toLocaleString('en-IN'),
    icon: UserCheck,
    accent: 'text-green-700'
  }, {
    label: 'Outstanding Dues',
    value: formatINR(stats.totalOutstanding),
    icon: IndianRupee,
    accent: 'text-club-burgundy'
  }, {
    label: 'New This Month',
    value: stats.newThisMonth.toLocaleString('en-IN'),
    icon: UserPlus,
    accent: 'text-club-gold'
  }];
  const fade = {
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="max-w-7xl mx-auto space-y-8" data-id="element-373">
      <div data-id="element-374">
        <span className="eyebrow block mb-1" data-id="element-375">Overview</span>
        <h1 className="text-3xl font-serif text-club-green" data-id="element-376">Dashboard</h1>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-377">
        {kpis.map((kpi, i) => {
        const Icon = kpi.icon;
        return <motion.div key={kpi.label} {...fade} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="bg-white border border-club-border rounded-sm p-5 flex items-start justify-between" data-id="element-378">
              <div data-id="element-379">
                <p className="text-xs uppercase tracking-wider text-club-ink/50 mb-2" data-id="element-380">
                  {kpi.label}
                </p>
                <p className={`text-2xl font-serif ${kpi.accent}`} data-id="element-381">
                  {kpi.value}
                </p>
              </div>
              <div className="p-2 bg-club-canvasAlt rounded-sm" data-id="element-382">
                <Icon size={20} className="text-club-gold" data-id="element-383" />
              </div>
            </motion.div>;
      })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-384">
        {/* Tier breakdown */}
        <div className="bg-white border border-club-border rounded-sm p-6" data-id="element-385">
          <h2 className="text-lg font-serif text-club-green mb-6" data-id="element-386">
            Membership by Tier
          </h2>
          <div className="space-y-4" data-id="element-387">
            {stats.tierBreakdown.map(t => <div key={t.tier} data-id="element-388">
                <div className="flex justify-between text-sm mb-1" data-id="element-389">
                  <span className="text-club-ink/80" data-id="element-390">{t.tier}</span>
                  <span className="font-medium text-club-ink" data-id="element-391">
                    {t.count.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="h-2.5 bg-club-canvasAlt rounded-full overflow-hidden" data-id="element-392">
                  <div className="h-full bg-club-gold rounded-full" style={{
                width: `${t.count / maxTier * 100}%`
              }} data-id="element-393" />
                </div>
              </div>)}
          </div>
          <div className="mt-6 pt-4 border-t border-club-border grid grid-cols-3 gap-2 text-center" data-id="element-394">
            <div data-id="element-395">
              <p className="text-lg font-serif text-green-700" data-id="element-396">
                {stats.activeCount.toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-club-ink/50" data-id="element-397">Active</p>
            </div>
            <div data-id="element-398">
              <p className="text-lg font-serif text-club-gold" data-id="element-399">
                {stats.pendingCount.toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-club-ink/50" data-id="element-400">Pending</p>
            </div>
            <div data-id="element-401">
              <p className="text-lg font-serif text-club-burgundy" data-id="element-402">
                {stats.suspendedCount.toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-club-ink/50" data-id="element-403">Suspended</p>
            </div>
          </div>
        </div>

        {/* Members with dues */}
        <div className="bg-white border border-club-border rounded-sm p-6" data-id="element-404">
          <div className="flex items-center justify-between mb-5" data-id="element-405">
            <h2 className="text-lg font-serif text-club-green" data-id="element-406">
              Highest Outstanding Dues
            </h2>
            <Link to="/admin/members" className="text-sm text-club-gold hover:text-club-green transition-colors flex items-center gap-1" data-id="element-407">
              View all <ArrowRight size={14} data-id="element-408" />
            </Link>
          </div>
          <div className="divide-y divide-club-border" data-id="element-409">
            {membersWithDues.map(m => <Link key={m.id} to={`/admin/members/${m.id}`} className="flex items-center justify-between py-3 hover:bg-club-canvasAlt/40 -mx-2 px-2 rounded-sm transition-colors" data-id="element-410">
                <div data-id="element-411">
                  <p className="text-sm font-medium text-club-ink" data-id="element-412">{m.name}</p>
                  <p className="text-xs text-club-ink/50" data-id="element-413">{m.id}</p>
                </div>
                <span className="text-sm font-medium text-club-burgundy" data-id="element-414">
                  {formatINR(m.outstandingBalance)}
                </span>
              </Link>)}
          </div>
        </div>
      </div>

      {/* Recent members */}
      <div className="bg-white border border-club-border rounded-sm p-6" data-id="element-415">
        <div className="flex items-center justify-between mb-5" data-id="element-416">
          <h2 className="text-lg font-serif text-club-green" data-id="element-417">
            Recently Joined
          </h2>
          <Link to="/admin/members" className="text-sm text-club-gold hover:text-club-green transition-colors flex items-center gap-1" data-id="element-418">
            All members <ArrowRight size={14} data-id="element-419" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-420">
          {recentMembers.map(m => <Link key={m.id} to={`/admin/members/${m.id}`} className="flex items-center gap-3 p-3 border border-club-border rounded-sm hover:border-club-gold transition-colors" data-id="element-421">
              <div className="h-9 w-9 rounded-full bg-club-green text-club-gold flex items-center justify-center font-serif shrink-0" data-id="element-422">
                {m.name.charAt(0)}
              </div>
              <div className="min-w-0" data-id="element-423">
                <p className="text-sm font-medium text-club-ink truncate" data-id="element-424">
                  {m.name}
                </p>
                <p className="text-xs text-club-ink/50" data-id="element-425">
                  {m.id} · Joined {m.joinedDate}
                </p>
              </div>
            </Link>)}
        </div>
      </div>
    </div>;
}